import type { LifecycleTarget, RealtimeSource } from '@components/Gala/seatAssignment';

/**
 * Keeps the public seating view current (mapd-issues#654, user stories 33–35).
 *
 * The view is read-only, so a change is taken as a cue to refetch rather than
 * applied in place: a Table rename arrives as one event per seated attendee,
 * and those are coalesced into one refetch. Events published while the socket
 * was down are gone, so a restore refetches too, as does returning to the tab.
 * There is deliberately no background poll (ADR-0004).
 */
export interface LiveSeatingOptions {
  eventId: string;
  /** Refetch the Seat Assignments and redraw the room. */
  refresh: () => void;
  /** Where the realtime events arrive. Defaults to `window`. */
  source?: RealtimeSource;
  /** Usually `document`. Source of `visibilitychange`. */
  visibility?: LifecycleTarget & { visibilityState?: string };
  /** Coalescing window for a burst of seat events. Defaults to 500ms. */
  debounceMs?: number;
}

const SEAT_EVENT_TYPES = ['tableStatus', 'attendeeProfile'];

export function attachLiveSeating(options: LiveSeatingOptions): () => void {
  const { eventId, refresh, debounceMs = 500 } = options;
  const source =
    options.source ?? (typeof window !== 'undefined' ? (window as unknown as RealtimeSource) : undefined);
  const visibility =
    options.visibility ??
    (typeof document !== 'undefined'
      ? (document as unknown as LifecycleTarget & { visibilityState?: string })
      : undefined);

  let timer: ReturnType<typeof setTimeout> | null = null;
  let recoveryOwed = false;

  const refreshNow = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    refresh();
  };

  const onSeatEvent = (event: Event) => {
    const detail = (event as CustomEvent).detail as { payload?: Record<string, unknown> } | undefined;
    const carried = detail?.payload?.Event__c;
    if (typeof carried === 'string' && carried.length > 0 && carried !== eventId) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(refreshNow, debounceMs);
  };
  const onDisconnected = () => {
    recoveryOwed = true;
  };
  const onConnected = () => {
    if (!recoveryOwed) return;
    recoveryOwed = false;
    refreshNow();
  };
  const onVisibilityChange = () => {
    if (visibility?.visibilityState !== 'hidden') refreshNow();
  };

  SEAT_EVENT_TYPES.forEach((type) => source?.addEventListener(type, onSeatEvent));
  source?.addEventListener('realtimeConnected', onConnected);
  source?.addEventListener('realtimeDisconnected', onDisconnected);
  visibility?.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    if (timer) clearTimeout(timer);
    SEAT_EVENT_TYPES.forEach((type) => source?.removeEventListener(type, onSeatEvent));
    source?.removeEventListener('realtimeConnected', onConnected);
    source?.removeEventListener('realtimeDisconnected', onDisconnected);
    visibility?.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
